/**
 * Morse — der Reiter „Fortschritt“. (06.10.2026, Morse-Überarbeitung)
 *
 * Ruhig, nur das Wichtige (Martin: „jede Seite komplett überarbeiten“; Vorgabe: „kein Datengrab“):
 *   - heute geübt, EIN Satz, was als Nächstes dran ist (06.10.2026, morse-texte.ts `alsNaechstes`), die Stufe
 *     und wie weit es bis zum nächsten Zeichen ist;
 *   - eine kleine Kurve: die letzten Hörrunden in Prozent, die 90-%-Linie der Koch-Methode;
 *   - die Zeichen der Stufe als Kacheln (antippen = anhören), je Kachel wie sicher es sitzt;
 *   - „Stufe ändern“ (08.10.2026): eine Stufe zurück oder eine Stufe bis zur höchsten erreichten wählen (morse-stufe.ts);
 *   - die letzten fünf Übungen.
 * Tempo und Ton stehen eingeklappt unter allen Reitern (morse-view.ts).
 *
 * ⚠️ Die Kurve ist ein SVG mit Klassen — Farben aus style.css, kein `style`-Attribut.
 */
import { KOCH_LETZTE_STUFE, KOCH_MINDEST_NEU, KOCH_REIHENFOLGE, KOCH_SCHWELLE, kochNeueZeichen, kochNeuesZeichen, kochStufe, kochStufenZiel, kochZeichen } from "../../shared/morse-koch.js";
import { stufenSicherheit } from "./morse-runde.js";
import { MorseStufe } from "./morse-stufe.js";
import { alsNaechstes } from "./morse-texte.js";
import { baueMeldung, el, gueteBalken, prozent, zeichenKnopf, type MorseStandDaten, type Sitzung, type StufeAnschluss } from "./morse-teile.js";

const ZEIT = new Intl.DateTimeFormat("de-AT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" });
const SVG = "http://www.w3.org/2000/svg";
/** So viele Hörrunden zeigt die Kurve. */
const KURVE_RUNDEN = 12;
/** So viele Übungen zeigt die Liste. */
const LISTE_UEBUNGEN = 5;

function quote(x: Sitzung): number | null {
  const n = x.gesamt + x.zuviel;
  return n ? x.richtig / n : null;
}

export class MorseFortschritt {
  private readonly heute: HTMLElement;
  private readonly naechstes: HTMLElement;
  private readonly stufe: HTMLElement;
  private readonly kurve: HTMLElement;
  private readonly kacheln: HTMLElement;
  private readonly liste: HTMLElement;
  /** Meldet hier, wenn beim Anhören kein Ton kam — bei den Kacheln, wo Martin hinschaut. */
  private readonly meldung: HTMLElement;
  /** „Stufe ändern“ — ohne Anschluss (ältere Aufrufer, Tests) fehlt der Block. */
  private readonly stufenWahl: MorseStufe | null;

  constructor(
    tafel: HTMLElement,
    private readonly spiele: (z: string, wo: HTMLElement) => void,
    stufe?: StufeAnschluss,
  ) {
    const kopf = el("section", "morse-block morse-block--kopf");
    kopf.setAttribute("aria-label", "Heute und Stufe");
    this.heute = el("p", "morse-heute");
    this.naechstes = el("p", "morse-naechstes");
    this.stufe = el("p", "morse-hinweis");
    kopf.append(this.heute, this.naechstes, this.stufe);

    const kurveBlock = el("section", "morse-block");
    const kurveTitel = el("h3", "morse-block-titel", "Deine Hörrunden");
    kurveTitel.id = "morse-kurve-titel";
    kurveBlock.setAttribute("aria-labelledby", kurveTitel.id);
    this.kurve = el("div", "morse-kurve");
    kurveBlock.append(kurveTitel, this.kurve);

    const zeichenBlock = el("section", "morse-block");
    const zeichenTitel = el("h3", "morse-block-titel", "Deine Zeichen");
    zeichenTitel.id = "morse-zeichen-titel";
    zeichenBlock.setAttribute("aria-labelledby", zeichenTitel.id);
    this.kacheln = el("div", "morse-kacheln");
    this.kacheln.setAttribute("role", "group");
    this.kacheln.setAttribute("aria-label", "Die Zeichen deiner Stufe — antippen zum Anhören");
    this.meldung = baueMeldung();
    zeichenBlock.append(zeichenTitel, el("p", "morse-hinweis", "Wie sicher jedes Zeichen sitzt — antippen zum Anhören."), this.kacheln, this.meldung);

    const listeBlock = el("section", "morse-block");
    const listeTitel = el("h3", "morse-block-titel", "Letzte Übungen");
    listeTitel.id = "morse-liste-titel";
    listeBlock.setAttribute("aria-labelledby", listeTitel.id);
    this.liste = el("ul", "morse-sitzungen");
    listeBlock.append(listeTitel, this.liste);

    this.stufenWahl = stufe ? new MorseStufe(stufe) : null;
    tafel.append(kopf, kurveBlock, zeichenBlock, ...(this.stufenWahl ? [this.stufenWahl.wurzel] : []), listeBlock);
  }

  zeichne(s: MorseStandDaten): void {
    this.heute.textContent =
      s.heute.sitzungen === 0
        ? "Heute noch nicht geübt."
        : `Heute: ${s.heute.sitzungen} ${s.heute.sitzungen === 1 ? "Übung" : "Übungen"}, ${s.heute.minuten} ${s.heute.minuten === 1 ? "Minute" : "Minuten"}.`;
    const sicher = stufenSicherheit(s.stufe, s.zeichen);
    const st = kochStufe(s.stufe);
    this.naechstes.textContent = alsNaechstes({
      sicher,
      neu: kochNeueZeichen(st),
      danach: st < KOCH_LETZTE_STUFE ? KOCH_REIHENFOLGE[st + 1] : null,
      prozent,
      ziel: s.fenster ? kochStufenZiel(st, s.fenster) : null,
    });
    // Die Regel in einfachen Worten — kurz, ohne Fachwort („9 von 10“ statt Quote und Fenster).
    this.stufe.textContent =
      `Stufe ${s.stufe} von ${KOCH_LETZTE_STUFE}, neu ist ${kochNeuesZeichen(s.stufe)}${sicher === null ? "" : `, im Mittel ${prozent(sicher)} sicher`}. ` +
      `Ein neues Zeichen kommt, wenn in deinen letzten Hörrunden 9 von 10 Zeichen stimmen — auch beim neuen Zeichen (es muss mindestens ${KOCH_MINDEST_NEU}-mal gekommen sein).`;
    this.zeichneKurve(s.sitzungen.filter((x) => x.art !== "geben"));
    this.zeichneKacheln(s);
    this.stufenWahl?.zeichne(s);
    this.zeichneListe(s.sitzungen);
  }

  private zeichneKurve(hoeren: Sitzung[]): void {
    const k = this.kurve;
    k.textContent = "";
    const punkte = hoeren
      .slice(0, KURVE_RUNDEN)
      .reverse()
      .map(quote)
      .filter((q): q is number => q !== null);
    if (!punkte.length) {
      k.appendChild(el("p", "morse-hinweis", "Noch keine Hörrunde — nach der ersten steht hier deine Kurve."));
      return;
    }
    const B = 300;
    const H = 90;
    const rand = 8;
    const x = (i: number): number => (punkte.length === 1 ? B / 2 : rand + (i * (B - 2 * rand)) / (punkte.length - 1));
    // Die Achse beginnt knapp unter dem schwächsten Wert (höchstens bei 50 %) — sonst klebt alles oben am Rand.
    const unten = Math.max(0, Math.min(0.5, Math.floor((Math.min(...punkte) - 0.05) * 10) / 10));
    const y = (q: number): number => rand + ((1 - q) / (1 - unten)) * (H - 2 * rand);
    const svg = document.createElementNS(SVG, "svg");
    svg.setAttribute("viewBox", `0 0 ${B} ${H}`);
    svg.setAttribute("class", "morse-kurve-bild");
    svg.setAttribute("role", "img");
    const letzte = punkte[punkte.length - 1];
    svg.setAttribute("aria-label", `Die letzten ${punkte.length} Hörrunden: zuletzt ${prozent(letzte)}, bestes ${prozent(Math.max(...punkte))}. Linie bei ${prozent(KOCH_SCHWELLE)}.`);
    const linie = document.createElementNS(SVG, "line");
    linie.setAttribute("class", "morse-kurve-schwelle");
    linie.setAttribute("x1", "0");
    linie.setAttribute("x2", String(B));
    linie.setAttribute("y1", String(y(KOCH_SCHWELLE)));
    linie.setAttribute("y2", String(y(KOCH_SCHWELLE)));
    svg.appendChild(linie);
    if (punkte.length > 1) {
      const pfad = document.createElementNS(SVG, "polyline");
      pfad.setAttribute("class", "morse-kurve-linie");
      pfad.setAttribute("points", punkte.map((q, i) => `${x(i).toFixed(1)},${y(q).toFixed(1)}`).join(" "));
      svg.appendChild(pfad);
    }
    punkte.forEach((q, i) => {
      const c = document.createElementNS(SVG, "circle");
      c.setAttribute("class", q >= KOCH_SCHWELLE ? "morse-kurve-punkt morse-kurve-punkt--erreicht" : "morse-kurve-punkt");
      c.setAttribute("cx", x(i).toFixed(1));
      c.setAttribute("cy", y(q).toFixed(1));
      c.setAttribute("r", i === punkte.length - 1 ? "4.5" : "3");
      svg.appendChild(c);
    });
    const legende = el("p", "morse-kurve-legende");
    legende.setAttribute("aria-hidden", "true");
    legende.append(el("span", "", `zuletzt ${prozent(letzte)}`), el("span", "morse-kurve-legende-linie", `Linie: ${prozent(KOCH_SCHWELLE)}`));
    k.append(svg, legende);
  }

  private zeichneKacheln(s: MorseStandDaten): void {
    const k = this.kacheln;
    k.textContent = "";
    const je = new Map(s.zeichen.map((z) => [z.zeichen, z]));
    const neue = kochNeueZeichen(s.stufe);
    for (const z of kochZeichen(s.stufe)) {
      const d = je.get(z);
      const gehoert = (d?.gehoert ?? 0) > 0;
      const kachel = el("div", "morse-kachel");
      const b = zeichenKnopf(z, () => this.spiele(z, this.meldung), { neu: neue.includes(z), zusatz: gehoert ? `${prozent(d!.guete)} sicher` : "noch nicht gehört" });
      kachel.appendChild(b);
      kachel.appendChild(gueteBalken("morse-kachel-balken", gehoert ? d!.guete : 0));
      k.appendChild(kachel);
    }
  }

  private zeichneListe(sitzungen: Sitzung[]): void {
    const l = this.liste;
    l.textContent = "";
    if (!sitzungen.length) {
      l.appendChild(el("li", "morse-hinweis", "Noch keine Übung gespeichert."));
      return;
    }
    for (const x of sitzungen.slice(0, LISTE_UEBUNGEN)) {
      const q = quote(x);
      const li = el("li", "morse-sitzung");
      li.append(
        el("span", "morse-sitzung-zeit", ZEIT.format(new Date(x.zeitpunkt))),
        el("span", "morse-sitzung-art", `${x.art === "hoeren" ? "Hören" : x.art === "sprechen" ? "Augen zu" : "Geben"} · Stufe ${x.stufe}`),
        el("span", "morse-sitzung-wert", q === null ? "–" : prozent(q)),
      );
      l.appendChild(li);
    }
  }
}
